/**
 * DATA QUALITY — six ratios about the RECORD, not about the building.
 *
 * This is the only panel on the screen that can tell an owner the rest of the
 * screen is unreliable. A resolution rate of 4% means the roster links behind
 * every "who was this visitor for" answer barely exist, and no count anywhere
 * else says so.
 *
 * ── EVERY RATIO CARRIES ITS RAW PAIR AND A WAY INTO THE ROWS ──
 * A percentage with no denominator cannot be sized: 67% of three entries and 67%
 * of three thousand are the same number and different evidence. And a quality
 * number with no worklist behind it changes nothing — so every ratio the server
 * can drill into gets a control that shows the offending rows, inline, without
 * taking the ratios off the screen.
 *
 * ── NULL IS NOT ZERO, AND THE EDIT LATENCY IS WHY THIS IS WRITTEN DOWN ──
 * The server explicitly fixed a bug where GREATEST(0, NULL) returned 0 for the
 * edit latency and produced a confident "every mistake is caught instantly" over
 * a window in which nothing had been corrected at all. A null ratio here is a
 * zero DENOMINATOR and renders as an absence with its own dashed track and its
 * own sentence. It never renders as 0%.
 */
import React from 'react';
import {
  AnalyticsQuality,
  AnalyticsQualityDrill,
  AnalyticsQualityRatio,
} from '../../../../../api/analytics-client';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { ChartError, ChartSkeleton } from '../charts/chart-empty';
import {
  DataTable,
  PanelToggle,
  TableScroller,
  Td,
  Th,
} from '../charts/chart.styled';
import { format } from '../charts/chart-theme';
import { clockNote } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';
import {
  DrillRegion,
  MeterAbsent,
  MeterFill,
  MeterHead,
  MeterItem,
  MeterList,
  MeterMeta,
  MeterName,
  MeterRaw,
  MeterTrack,
  MeterValue,
  PanelSectionTitle,
  SectionCaption,
  StatePill,
} from './panels.styled';
import { hubNotes } from './panel-utils';
import { Collapse } from '../../../../../motion';

const PANEL_ID = 'quality';

/** Per-ratio copy the wire cannot carry: the short name for the meter, and the
 *  sentence that has to be true where the number is. The server ships the
 *  `label` (what the ratio measures) and the key; this adds what the number
 *  MEANS when it is high, and what a null one means.
 *
 *  A key with no entry here still renders — humanise(key) is a readable name and
 *  the server's own label carries the rest — so a seventh ratio appears as a real
 *  meter rather than as a crash. */
const RATIO_COPY: Record<string, { name: string; high: string; absent: string }> = {
  scan_adoption: {
    name: 'Scan adoption',
    high: 'High is good: the name came off a document rather than out of somebody typing it at 2am.',
    absent:
      'No entry in this period carries a name-capture record, so there is nothing to measure. That is not 0% adoption.',
  },
  correction_rate: {
    name: 'Correction rate',
    high: 'This is NOT an error rate. A corrected record is a record somebody improved; the number worth watching is how many of those overrode a SCAN, which is the only measure of the scanner accuracy this product has.',
    absent:
      'Nothing was recorded in this period, so there is nothing that could have been corrected.',
  },
  resolution_rate: {
    name: 'Resolution rate',
    high: 'High is good: the entry is linked to a real occupancy, so every "who was this for" answer behind it is real.',
    absent:
      'Nothing was recorded in this period, so there is nothing to match to an occupancy.',
  },
  auto_close_rate: {
    name: 'Auto-close rate',
    high: 'LOW is good. An auto-close is a DESK-PROCESS GAP, not a departure: the nightly sweep stamped 23:59 because nobody signed the visit out, so nobody observed anything ending.',
    absent:
      'Nothing in this period was closed at all, by a person or by the sweep, so there is no gap to measure.',
  },
  close_rate: {
    name: 'Close rate',
    high: 'High is good: building activities opened in this period were seen through rather than left open.',
    absent:
      'No building activity was opened in this period, so there is nothing that could have been closed.',
  },
  edit_latency: {
    name: 'Corrected within a day',
    high: 'High is good: a wrong record did not stay wrong for long.',
    absent:
      'Nothing in this period has been corrected yet, so there is no time-to-correction to report. Not yet corrected is not the same as corrected instantly.',
  },
};

const nameOf = (key: string): string => RATIO_COPY[key]?.name || format.humanise(key);

/** The extra facts the server attaches to ONE ratio, rendered as text beside it.
 *  Each is a separate sentence rather than a second number on the meter, because
 *  each answers a different question — "how many overrode a scan" is not a
 *  fraction of the correction rate. */
const RatioExtras: React.FC<{ ratio: AnalyticsQualityRatio }> = ({ ratio }) => (
  <>
    {ratio.noProvenance != null && ratio.noProvenance > 0 ? (
      <MeterMeta data-testid="quality-no-provenance">
        <span>
          {ratio.noProvenance.toLocaleString()} entries carry no capture record at all,
          so they are outside this ratio in both directions rather than counted as
          typed by hand.
        </span>
      </MeterMeta>
    ) : null}

    {ratio.scanOverrides != null ? (
      <MeterMeta>
        <span>
          {ratio.edits?.toLocaleString() ?? '0'} corrections in total,{' '}
          <strong>{ratio.scanOverrides.toLocaleString()} of them overrode a scan</strong>.
          {ratio.topFields && ratio.topFields.length
            ? ` Most corrected: ${ratio.topFields
                .map(field => `${field.field} (${field.count})`)
                .join(', ')}.`
            : ''}
        </span>
      </MeterMeta>
    ) : null}

    {/* UNSET AND NO_MATCH ARE OPPOSITE FACTS AND ARE NEVER SUMMED. `unset` is
        nobody ever tried, which is a worklist. `no_match` is somebody tried and
        there is no such resident, which is finished work and nothing to fix. One
        "unresolved" figure would hide which of the two it was. */}
    {ratio.unset != null ? (
      <MeterMeta data-testid="quality-resolution-split">
        <StatePill tone={ratio.unset > 0 ? 'warning' : 'neutral'}>
          {ratio.unset.toLocaleString()} nobody has tried to match
        </StatePill>
        <StatePill tone="neutral">
          {(ratio.noMatch ?? 0).toLocaleString()} tried, no such resident
        </StatePill>
        <span>
          These are not the same thing and are never added together — only the first
          is work.
        </span>
      </MeterMeta>
    ) : null}

    {ratio.visitors && ratio.activities ? (
      <MeterMeta>
        <span>
          Visits {format.ratio(ratio.visitors.ratio)} (
          {format.fraction(ratio.visitors.numerator, ratio.visitors.denominator)}) ·
          Building activities {format.ratio(ratio.activities.ratio)} (
          {format.fraction(ratio.activities.numerator, ratio.activities.denominator)}).
          Rows closed by a migration are in neither half.
        </span>
      </MeterMeta>
    ) : null}

    {ratio.stillOpen != null ? (
      <MeterMeta>
        <StatePill tone={ratio.stillOpen > 0 ? 'warning' : 'neutral'}>
          {ratio.stillOpen.toLocaleString()} still open
        </StatePill>
      </MeterMeta>
    ) : null}

    {/* THE NULL THAT MUST NOT BE A ZERO. p50/p90 are null for "never corrected",
        and the bug the server fixed was rendering exactly that as 0. */}
    {ratio.key === 'edit_latency' ? (
      <MeterMeta data-testid="quality-edit-latency-times">
        {ratio.p50 == null && ratio.p90 == null ? (
          <em>
            Not yet corrected — nothing in this period has been corrected, so there is
            no time to report.
          </em>
        ) : (
          <span>
            Typical time to correction {format.seconds(ratio.p50 ?? null)} · slowest
            tenth {format.seconds(ratio.p90 ?? null)}
            {ratio.thresholdHours
              ? `. The ratio above is corrections made within ${ratio.thresholdHours}h.`
              : ''}
          </span>
        )}
      </MeterMeta>
    ) : null}
  </>
);

const DrillRows: React.FC<{ drill: AnalyticsQualityDrill; tz: string; label: string }> = ({
  drill,
  tz,
  label,
}) => (
  <>
    <PanelSectionTitle>{drill.label}</PanelSectionTitle>
    <SectionCaption>
      {/* THE CAP IS A LIST LIMIT, NOT THE COUNT. Printing only the rows would let
          a reader conclude there are eight offending records when there are eight
          hundred. */}
      {drill.matched.toLocaleString()} row{drill.matched === 1 ? '' : 's'} match, inside
      the same period as the ratios above.
      {drill.truncated
        ? ` The ${drill.items.length} shown are a capped list, not the total.`
        : ''}
    </SectionCaption>
    {drill.items.length ? (
      <TableScroller>
        <DataTable data-testid="quality-drill-table">
          <thead>
            <tr>
              <Th scope="col">Building</Th>
              <Th scope="col">Source</Th>
              <Th scope="col">Recorded ({label})</Th>
              <Th scope="col">State</Th>
            </tr>
          </thead>
          <tbody>
            {drill.items.map(row => (
              <tr key={`${row.source}-${row.id}`}>
                <Td>{row.building}</Td>
                <Td>{format.humanise(row.source)}</Td>
                <Td>{format.instant(row.at, tz)}</Td>
                <Td>
                  {[
                    row.resolution ? `resolution: ${row.resolution}` : '',
                    row.closeSource ? `closed by: ${row.closeSource}` : '',
                    row.closed ? 'closed' : 'open',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </TableScroller>
    ) : (
      <SectionCaption>
        No rows match, so there is nothing behind this ratio to act on.
      </SectionCaption>
    )}
  </>
);

export interface QualityPanelProps {
  state: ChartShellState;
  data?: AnalyticsQuality;
  /** The open drill-down, from the URL, so it is shareable. */
  drill: string | null;
  onDrill: (key: string | null) => void;
  drillData?: AnalyticsQualityDrill;
  drillLoading: boolean;
  drillError?: string;
  onExportCsv?: () => void;
}

export const QualityPanel: React.FC<QualityPanelProps> = ({
  state,
  data,
  drill,
  onDrill,
  drillData,
  drillLoading,
  drillError,
  onExportCsv,
}) => {
  const notes = data ? hubNotes(data) : [];
  /* The answer's own clock (`tz`, R-T12) — decided here it used to fall to the
     one zone in scope, and to UTC, silently, when there were several. */
  const clock = useClock({
    response: data ? { tz: data.tz || data.window?.tz, tz_source: data.tz_source } : null,
  });
  const tz = clock.zone;

  return (
    <PanelShell
      id={PANEL_ID}
      title="Data quality"
      /* SIX METERS IN A ROW OF ONE. Every other panel on this tab is full
         width, and a half-width quality panel stacks its ratios into a column
         of six while the column beside it is empty. */
      wide
      state={state}
      notes={notes}
      onExportCsv={onExportCsv}
      caveat="These six are about the RECORD, not about the building — they say how reliable every other figure on this screen is. Every denominator is entries CREATED in this period. An auto-close is a desk-process gap, not a departure: nobody watched anything end."
      emptyReason={
        data && !data.ratios.length
          ? {
              title: 'No ratios were returned',
              text: 'The server answered without any quality ratios, so there is nothing to show and nothing to infer.',
            }
          : null
      }
    >
      {data ? (
        <>
          <MeterMeta data-testid="quality-entries">
            <span>
              {data.entries.toLocaleString()} entr{data.entries === 1 ? 'y' : 'ies'}{' '}
              created in this period
              {Object.keys(data.bySource || {}).length
                ? ` — ${Object.entries(data.bySource)
                    .map(([source, count]) => `${format.humanise(source)} ${count.toLocaleString()}`)
                    .join(', ')}`
                : ''}
              .
            </span>
          </MeterMeta>

          <MeterList data-testid="quality-meters">
            {data.ratios.map(ratio => {
              const copy = RATIO_COPY[ratio.key];
              const absent = ratio.ratio == null;
              const open = drill === ratio.key;

              return (
                <MeterItem key={ratio.key} data-testid={`quality-${ratio.key}`}>
                  <MeterHead>
                    <MeterName>{nameOf(ratio.key)}</MeterName>
                    <MeterValue>
                      {format.ratio(ratio.ratio)}
                      <MeterRaw>
                        {format.fraction(ratio.numerator, ratio.denominator)}
                      </MeterRaw>
                    </MeterValue>
                  </MeterHead>

                  {/* THE BAR AGAINST THE TRACK — or, when there is no ratio, a
                      dashed hollow track. An empty solid track is the same
                      picture as 0%, and 0% is a claim this data does not make. */}
                  {absent ? (
                    <MeterAbsent
                      role="img"
                      aria-label={`${nameOf(ratio.key)}: not measured in this period`}
                      data-testid={`quality-absent-${ratio.key}`}
                    />
                  ) : (
                    <MeterTrack
                      role="img"
                      aria-label={`${nameOf(ratio.key)}: ${format.ratio(
                        ratio.ratio
                      )}, ${format.fraction(ratio.numerator, ratio.denominator)}`}
                    >
                      <MeterFill pct={ratio.ratio ?? 0} />
                    </MeterTrack>
                  )}

                  <MeterMeta>
                    <span>{ratio.label}</span>
                  </MeterMeta>
                  {absent ? (
                    <MeterMeta data-testid={`quality-absent-note-${ratio.key}`}>
                      <em>
                        {copy?.absent ||
                          'Nothing in this period makes this measurable, so there is no ratio rather than a ratio of zero.'}
                      </em>
                    </MeterMeta>
                  ) : copy ? (
                    <MeterMeta>
                      <span>{copy.high}</span>
                    </MeterMeta>
                  ) : null}

                  <RatioExtras ratio={ratio} />

                  {/* THE WAY INTO THE ROWS. Only offered when the server said this
                      ratio HAS rows behind it — an inert control on the two that
                      do not would be a promise the endpoint refuses. */}
                  {ratio.drill ? (
                    <MeterMeta>
                      <PanelToggle
                        type="button"
                        active={open}
                        aria-expanded={open}
                        aria-controls={`quality-drill-${ratio.key}`}
                        data-testid={`quality-drill-toggle-${ratio.key}`}
                        onClick={() => onDrill(open ? null : ratio.drill)}
                      >
                        {open ? 'Hide the rows' : 'Show the rows'}
                      </PanelToggle>
                    </MeterMeta>
                  ) : null}

                  <Collapse open={open} id={`quality-drill-${ratio.key}`}>
                    <DrillRegion data-testid="quality-drill">
                      {drillLoading ? (
                        <ChartSkeleton height={80} />
                      ) : drillError ? (
                        <ChartError
                          title="These rows could not be loaded"
                          message={drillError}
                        />
                      ) : drillData ? (
                        <DrillRows drill={drillData} tz={tz} label={clockNote(clock)} />
                      ) : null}
                    </DrillRegion>
                  </Collapse>
                </MeterItem>
              );
            })}
          </MeterList>
        </>
      ) : null}
    </PanelShell>
  );
};
